import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiSend, FiChevronLeft } from 'react-icons/fi';

const MAX_CHARS = 1000;

export default function WritePage({ onSend, initialData, category, onBack }) {
  const [content, setContent] = useState(initialData.content || '');
  const [dateString, setDateString] = useState('');
  const textareaRef = useRef(null);
  const renderDivRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
    const today = new Date();
    const formattedDate = `${today.getFullYear()} . ${(today.getMonth() + 1).toString().padStart(2, '0')} . ${today.getDate().toString().padStart(2, '0')}`;
    setDateString(formattedDate);
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    if (val.length <= MAX_CHARS) {
      setContent(val);
    }
  };

  const handleScroll = (e) => {
    if (renderDivRef.current) {
      renderDivRef.current.scrollTop = e.target.scrollTop;
    }
  };

  const handleSubmit = () => {
    if (content.trim().length === 0) return;
    onSend({ content });
  };

  const isReady = content.trim().length > 0;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 0.95, filter: 'blur(10px)' }}
      transition={{ duration: 0.8 }}
      className="relative z-10 w-full max-w-2xl px-5 md:px-8 h-full py-6 md:py-10 flex flex-col"
    >
      {/* 頂部導覽列 */}
      <div className="shrink-0 relative flex items-center justify-center mb-6 md:mb-8 min-h-[3.5rem]">
        <button 
          onClick={onBack}
          className="absolute left-0 text-ink-brown/40 hover:text-ink-brown/80 transition-colors flex items-center gap-1 font-handwriting text-sm tracking-wider"
        >
          <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
          重新選擇
        </button>

        {/* 動態陪伴語與小 LOGO */}
        <div className="flex flex-col items-center">
          <span className="font-brush text-2xl text-ink-brown opacity-30 mb-1 ml-1 tracking-widest">輕舟</span>
          <h2 className="font-handwriting text-ink-brown opacity-60 text-sm md:text-base tracking-[0.2em] transition-opacity duration-700">
            {content.length === 0 ? category.emptyPrompt : category.typingPrompt}
          </h2>
        </div>
      </div>

      {/* 輸入區塊 */}
      <div className="relative flex-1 mb-4 md:mb-6 bg-white/40 rounded-sm shadow-[inset_0_0_20px_rgba(0,0,0,0.02)] border border-khaki-light/30 min-h-[150px] overflow-hidden">
        
        <div className="absolute top-5 right-6 text-ink-brown/30 font-handwriting text-sm md:text-base tracking-[0.2em] pointer-events-none z-10">
          {dateString}
        </div>

        <div 
          ref={renderDivRef}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full text-sync pt-14 no-scrollbar pointer-events-none z-10 text-[#3B2A20]"
        >
          {content.length === 0 ? (
            <span className="opacity-40">點擊此處開始。</span>
          ) : (
            content.endsWith('\n') ? content + ' ' : content
          )}
        </div>

        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleInput}
          onScroll={handleScroll}
          spellCheck="false"
          className="absolute inset-0 w-full h-full text-sync pt-14 no-scrollbar z-20 resize-none outline-none bg-transparent text-transparent caret-[#3B2A20]"
        />

        {/* 背景紙紋行線 */}
        <div className="absolute inset-x-4 md:inset-x-6 top-14 bottom-6 pointer-events-none z-0 border-t border-khaki-light/20 opacity-40">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: 'linear-gradient(to bottom, transparent calc(2.75rem - 1px), rgba(217, 205, 191, 0.2) 1px)',
              backgroundSize: '100% 2.75rem'
            }}
          ></div>
        </div>
      </div>

      {/* 底部按鈕區塊 */}
      <div 
        className="shrink-0 mt-auto pb-2 flex flex-col items-center transition-opacity duration-1000"
        style={{ opacity: isReady ? 1 : 0.3 }}
      >
        <button 
          onClick={handleSubmit}
          disabled={!isReady}
          className="w-full py-3.5 md:py-4 rounded-xl bg-gradient-to-b from-khaki-light/40 to-khaki/30 text-ink-brown font-handwriting text-base md:text-lg tracking-[0.2em] flex items-center justify-center gap-3 transition-all duration-500 hover:bg-khaki/40 disabled:cursor-not-allowed group"
        >
          <span>寫好了，讓它漂走</span>
          <SafeIcon icon={FiSend} className="w-4 h-4 opacity-60 transition-transform duration-500 group-hover:translate-x-2 group-hover:-translate-y-1 group-hover:opacity-100" />
        </button>
        <p className="text-center mt-3 text-ink-brown/60 font-handwriting text-xs md:text-sm tracking-wider">
          字句將隨紙船落水無痕，徹底消散於湖心之中。
        </p>
      </div>

    </motion.div>
  );
}